import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listDevices, revokeDevice } from '../api/devices.ts';
import { createPairingCode } from '../api/auth.ts';
import { formatRelative } from '../lib/format.ts';
import { toast } from '../store/toast.ts';
import { EmptyState } from '../components/common/EmptyState.tsx';
import { ConfirmDialog } from '../components/common/ConfirmDialog.tsx';
import { Modal } from '../components/common/Modal.tsx';
import { IconDevice, IconPlus } from '../lib/icons.tsx';
import type { Device } from '../api/types.ts';

const PLATFORM: Record<string, string> = {
  windows: 'Windows', darwin: 'macOS', linux: 'Linux',
};

export function DevicesPage() {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Device | null>(null);
  const [pairing, setPairing] = useState<{ code: string; expires_at: string } | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['devices'], queryFn: listDevices });

  const revoke = useMutation({
    mutationFn: revokeDevice,
    onSuccess: () => {
      toast.success('연결을 해제했습니다. 해당 PC 의 로컬 파일은 그대로 남습니다.');
      setPending(null);
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: (e: Error) => { toast.error(e); setPending(null); },
  });

  const pair = useMutation({
    mutationFn: createPairingCode,
    onSuccess: (result) => { setPairing(result); },
    onError: (e: Error) => { toast.error(e); },
  });

  const devices = data?.items ?? [];

  return (
    <div className="page">
      <div className="page__head">
        <h1 className="page__title">연결된 PC</h1>
        <div className="spacer" />
        <a className="btn" href="/download">클라이언트 내려받기</a>
        <button className="btn btn--primary" onClick={() => { pair.mutate(); }}>
          <IconPlus size={16} /> PC 연결하기
        </button>
      </div>

      <div className="page__body">
        {isLoading && <div className="empty"><span className="spinner" /></div>}
        {!isLoading && devices.length === 0 && (
          <EmptyState
            icon={<IconDevice size={40} className="faint" />}
            title="연결된 PC 가 없습니다"
            description="먼저 클라이언트를 내려받아 설치한 뒤, 'PC 연결하기'로 받은 8자리 코드를 입력하세요."
          />
        )}

        {devices.map((device) => (
          <div key={device.id} className="frow" style={{ gridTemplateColumns: '40px 1fr 1fr 160px' }}>
            <IconDevice size={20} className="ficon" />
            <div>
              <div className="row">
                <strong>{device.name}</strong>
                {device.is_current && <span className="frow__badge">현재 기기</span>}
              </div>
              <div className="faint" style={{ fontSize: 12 }}>
                {PLATFORM[device.platform] ?? device.platform} · v{device.client_version}
                {device.hostname && ` · ${device.hostname}`}
              </div>
            </div>
            <div className="frow__meta truncate" title={device.root_path ?? ''}>
              {device.root_path ?? '—'}
              {device.excluded_paths.length > 0 && (
                <div className="faint" style={{ fontSize: 11 }}>
                  제외 {device.excluded_paths.length}개 폴더
                </div>
              )}
            </div>
            <div className="row">
              <span className="frow__meta spacer">
                {device.last_seen_at ? formatRelative(device.last_seen_at) : '접속 기록 없음'}
              </span>
              <button className="btn btn--sm" onClick={() => { setPending(device); }}>
                연결 해제
              </button>
            </div>
          </div>
        ))}
      </div>

      {pairing && (
        <Modal title="PC 연결 코드" onClose={() => { setPairing(null); }}>
          <p className="modal__text">
            PC 클라이언트에서 아래 코드를 입력하세요. 10분 뒤 만료됩니다.
          </p>
          <div
            className="mono"
            style={{
              fontSize: 30, letterSpacing: '.18em', textAlign: 'center',
              padding: 'var(--sp-4)', background: 'var(--c-bg-sunken)',
              borderRadius: 'var(--r-md)',
            }}
          >
            {pairing.code}
          </div>
        </Modal>
      )}

      {pending && (
        <ConfirmDialog
          title="연결 해제"
          message={`'${pending.name}' 의 연결을 해제합니다.\n해당 PC 는 즉시 로그아웃되지만 이미 내려받은 파일은 지워지지 않습니다.`}
          confirmLabel="연결 해제" danger busy={revoke.isPending}
          onConfirm={() => { revoke.mutate(pending.id); }}
          onCancel={() => { setPending(null); }}
        />
      )}
    </div>
  );
}
